import * as vp from 'vopay';
import {
  Box,
  CardTabs,
  capitalizeFirstLetter,
  GetColorStatus,
  Title,
  Badge,
  Pagination,
  Tooltip,
  Icon,
  MaterialSymbol,
  formattedCurrency,
  usePagination,
  useUpdateQueryParams,
} from 'vopay';
import { FunctionComponent, JSX, useEffect, useMemo, useState } from 'react';
import styles from './ElinxHistory.module.scss';
import { useTranslation } from 'react-i18next';
import { format, parseISO, subDays } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useLocation } from 'react-router-dom';
import { useClientData } from '@/providers/ClientData.provider';
import useFetchELinxHistory from '@/hooks/useApi/useFetchELinxHistory';
import ElinxHistoryForm from './ElinxHistoryForm';
import useFileDownload from '@/hooks/useFiledDownload';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import { Button, CopyButton, TableFiltersContainer, TransactionStatus, Table } from 'UI';

import { useAlertDialog } from '@/providers/AlertDialog.provider';
import ElinxHistoryExport from './ElinxHistoryExport';

type TabContents = {
  account: JSX.Element;
  payment: JSX.Element;
};

type ElinxTab = keyof TabContents;

type QueryParams = {
  requestStatus: string;
  searchTerm: string;
  startDate: string;
  endDate: string;
  timeZone: string;
  elinxType: ElinxTab;
};

type ElinxSearchParams = {
  requestStatus?: string;
  searchTerm?: string;
  timeRange?: [vp.Timestamp, vp.Timestamp];
  timeZone?: string;
};

type FC = FunctionComponent;

const ElinxHistory: FC = () => {
  const {
    downloadPdfTable,
    downloadCsvFile,
    downloadJson,
    downloadXls,
    downloadDoc,
    downloadTxt,
    downloadXml,
    printFile,
  } = useFileDownload();
  const location = useLocation();
  const { userSettings } = useUserSettingsProvider();
  const { t } = useTranslation('translations', { keyPrefix: 'ElinxHistory' });

  const queryParams: QueryParams = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      timeZone: params.get('timeZone') || '',
      requestStatus: params.get('requestStatus') || '',
      searchTerm: params.get('searchTerm') || '',
      startDate: params.get('startDate') || '',
      endDate: params.get('endDate') || '',
      elinxType: (params.get('elinxType') as ElinxTab) || 'account',
    };
  }, [location.search]);

  const timeZone = useMemo(
    () => (queryParams.timeZone as vp.Timezone) || userSettings.timezone || 'UTC',
    [queryParams],
  );
  const requestStatus = useMemo(() => queryParams.requestStatus || '', [queryParams]);
  const searchTerm = useMemo(() => queryParams.searchTerm || '', [queryParams]);
  const startDate = useMemo(() => queryParams.startDate || '', [queryParams]);
  const endDate = useMemo(() => queryParams.endDate || '', [queryParams]);

  const timeDifference = useMemo(() => formatInTimeZone(subDays(new Date(), 7), timeZone, 'yyyy-MM-dd'), [timeZone]);
  const currentDateTime = useMemo(() => formatInTimeZone(new Date(), timeZone, 'yyyy-MM-dd'), [timeZone]);

  const filtersApplied = useMemo(() => {
    return Boolean(
      requestStatus ||
        searchTerm ||
        (timeZone && timeZone !== (userSettings.timezone || 'UTC')) ||
        (startDate && startDate !== timeDifference) ||
        (endDate && endDate !== currentDateTime),
    );
  }, [requestStatus, searchTerm, timeZone, userSettings.timezone, startDate, endDate, timeDifference, currentDateTime]);

  const updateQueryParams = useUpdateQueryParams<QueryParams>();
  const { response } = useClientData();
  const [filteredList, setFilteredList] = useState<vp.ELinxHistoryItem[]>([]);
  const [initialLoad, setInitialLoad] = useState(false);
  const [hasInvalidEndDate, setHasInvalidEndDate] = useState(false);
  const [shouldShowEndDateError, setShouldShowEndDateError] = useState(false);
  const [paylinkHistory, , , fetchPaylinkHistory] = useFetchELinxHistory({});
  const [paylinkHistoryExport, , , fetchPaylinkHistoryExport] = useFetchELinxHistory({});

  const [activeTab, setActiveTab] = useState<ElinxTab>(queryParams.elinxType || 'account');
  const [columnSwap, setColumnSwap] = useState<'name' | 'email'>('name');
  const [idSwap, setIdSwap] = useState<'transaction' | 'schedule'>('transaction');

  const alertDialog = useAlertDialog();

  const handleTabChange = (value: string) => {
    if (value === 'account' || value === 'payment') {
      setActiveTab(value);

      updateQueryParams({
        ...queryParams,
        elinxType: value,
      });
    }
  };

  // pagination
  const totalDataLength = useMemo(() => {
    return paylinkHistory.PaginationMetaData?.TotalRecords || 0;
  }, [paylinkHistory.ELinxRequests]);
  const PER_PAGE = 20;
  const { pageCount, pageIndex, setPageIndex } = usePagination(totalDataLength, {
    pageSize: PER_PAGE,
  });

  const search = async (offset = 0, searchParams: ElinxSearchParams = {}) => {
    const defaultValues = {
      PortalUser: response.LoginUserName,
      StartDateTime: searchParams.timeRange?.[0]
        ? format(searchParams.timeRange?.[0], 'yyyy-MM-dd')
        : startDate || timeDifference,
      EndDateTime: searchParams.timeRange?.[1]
        ? format(searchParams.timeRange?.[1], 'yyyy-MM-dd')
        : endDate || currentDateTime,
      Timezone: searchParams.timeZone ?? timeZone,
      ...(searchParams.requestStatus === 'all' ? {} : { ELinxStatus: searchParams.requestStatus }),
      ELinxRequestType: activeTab === 'account' ? 'beneficiary' : 'transaction',
      SearchText: searchParams.searchTerm ?? searchTerm,
      Limit: PER_PAGE,
      Offset: offset,
    };

    if (hasInvalidEndDate) {
      setShouldShowEndDateError(true);
      return;
    }
    try {
      const res = await fetchPaylinkHistory(defaultValues);

      if (!res.Success) throw new Error(res.ErrorMessage);
      const ELinxRequests = Object.values(res.ELinxRequests || {});
      setFilteredList(ELinxRequests);
      setInitialLoad(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('ErrorMessages.fetchingError');
      alertDialog.error(t('ErrorMessages.error'), msg);
      console.error(err);
    }
  };

  useEffect(() => {
    if (startDate > endDate) {
      setHasInvalidEndDate(true);
    } else {
      setHasInvalidEndDate(false);
    }
  }, [endDate]);

  useEffect(() => {
    const offset = setPageIndex(0);
    const searchParams = {
      requestStatus,
      searchTerm,
      startDate: startDate || timeDifference,
      endDate: endDate || currentDateTime,
      timeZone: timeZone,
    };
    updateQueryParams(searchParams);
    search(offset, searchParams);
  }, [activeTab]);

  const handleExport = async (exportType: 'pdf' | 'csv' | 'xls' | 'doc' | 'text' | 'json' | 'xml' | 'print') => {
    const defaultValues = {
      PortalUser: response.LoginUserName,
      StartDateTime: timeDifference,
      EndDateTime: currentDateTime,
      Timezone: timeZone,
      ELinxStatus: requestStatus,
      ELinxRequestType: activeTab === 'account' ? 'beneficiary' : '',
      SearchText: searchTerm,
    };
    const elinxHistoryRequest = defaultValues;
    elinxHistoryRequest.StartDateTime = startDate;
    elinxHistoryRequest.EndDateTime = endDate;
    const exportElinxData = await fetchPaylinkHistoryExport(elinxHistoryRequest);
    switch (exportType) {
      case 'pdf':
        downloadPdfTable('export-elinx-history-table', t('title'));
        break;
      case 'csv':
        downloadCsvFile('export-elinx-history-table', t('title'));
        break;
      case 'xls':
        downloadXls('export-elinx-history-table', t('title'));
        break;
      case 'doc':
        downloadDoc('export-elinx-history-table', t('title'));
        break;
      case 'text':
        downloadTxt('export-elinx-history-table', t('title'));
        break;
      case 'json':
        downloadJson(Object.values(exportElinxData.ELinxRequests || {}), t('title'));
        break;
      case 'xml':
        downloadXml('export-elinx-history-table', t('title'));
        break;
      case 'print':
        printFile('export-elinx-history-table');
        break;
      default:
        break;
    }
  };

  const tableProps: vp.TableProps<vp.ELinxHistoryItem> = {
    id: 'elinx-history-table',
    version: '2',
    zebra: true,
    columns: [
      {
        id: 'creationDate',
        header: t('Columns.creationDate'),
        align: 'left',
        field: 'RequestDate',
      },
      {
        id: 'eLinxID',
        header: t('Columns.elinxId'),
        align: 'left',
        template: ({ data }) => (
          <Box gap="sm" ai="center">
            <Table.Link
              to={{
                pathname: `/elinx-history/details/${data.ELinxRequestID}`,
                state: { eLinx: data },
              }}
            >
              {data.ELinxRequestID.toString()}
            </Table.Link>
            <CopyButton content={data.ELinxRequestID.toString()} color="rgba(45, 45, 45, 0.6)" />
          </Box>
        ),
      },
      {
        id: 'eLinxType',
        header: t('Columns.elinxType'),
        align: 'left',
        visible: activeTab === 'payment',
        template: ({ data }) => data.eLinxType || t('notApplicable'),
      },
      {
        id: 'requestStatus',
        header: t('Columns.requestStatus'),
        align: 'left',
        template: ({ data }) => (
          <Box flx="row" ai="center">
            <Box flx="row" ai="center">
              <TransactionStatus status={GetColorStatus(data.Status)} label={capitalizeFirstLetter(data.Status)} />
            </Box>
            {activeTab === 'payment' && data.Status === 'completed' && (
              <Box ps="sm">
                <Tooltip
                  type="hover"
                  information={t('TooltipMessages.requestCompleted')}
                  position="top"
                  hasArrow
                  icon="info"
                />
              </Box>
            )}
          </Box>
        ),
      },
      {
        id: 'recipientName',
        header: (
          <Box gap="sm" ai="center">
            {t('Columns.recipientName')}
            <MaterialSymbol icon={'switch_left'} fillAmount={1} size={'24px'} />
          </Box>
        ),
        align: 'left',
        visible: columnSwap === 'name',
        field: 'ReceiverName',
        onHeaderClick: () => {
          setColumnSwap('email');
        },
      },
      {
        id: 'recipientEmail',
        header: (
          <Box gap="sm" ai="center">
            {t('Columns.recipientEmail')}
            <MaterialSymbol icon={'switch_right'} fillAmount={1} size={'24px'} />
          </Box>
        ),
        align: 'left',
        visible: columnSwap === 'email',
        field: 'ReceiverEmailAddress',
        onHeaderClick: () => {
          setColumnSwap('name');
        },
      },
      {
        id: 'transactionID',
        header: (
          <Box gap="sm" ai="center">
            {t('Columns.transactionId')}
            <MaterialSymbol icon={'switch_left'} fillAmount={1} size={'24px'} />
          </Box>
        ),
        align: 'left',
        visible: activeTab === 'payment' && idSwap === 'transaction',
        template: ({ data }) => data.TransactionID?.toString() || t('notApplicable'),
        onHeaderClick: () => {
          setIdSwap('schedule');
        },
      },
      {
        id: 'scheduleID',
        header: (
          <Box gap="sm" ai="center">
            {t('Columns.scheduleId')}
            <MaterialSymbol icon={'switch_right'} fillAmount={1} size={'24px'} />
          </Box>
        ),
        align: 'left',
        visible: activeTab === 'payment' && idSwap === 'schedule',
        template: ({ data }) => data.ScheduledPaymentsID?.toString() || t('notApplicable'),
        onHeaderClick: () => {
          setIdSwap('transaction');
        },
      },
      {
        id: 'account',
        header: t('Columns.account'),
        align: 'left',
        template: ({ data }) =>
          data.Status === 'completed' ? (
            <>
              {data.AccountNumber && <span>{vp.getMaskedNumber(data.AccountNumber)}</span>}
              {data.CreditCardNumber && <span>{vp.getMaskedNumber(String(data.CreditCardNumber))}</span>}
            </>
          ) : (
            t('notApplicable')
          ),
      },
      {
        id: 'accountType',
        header: t('Columns.accountType'),
        align: 'left',
        visible: activeTab === 'account',
        template: ({ data }) =>
          data.Status === 'completed' ? (
            <>
              {data.AccountNumber && t('bankAccount')}
              {data.CreditCardNumber && t('creditCard')}
            </>
          ) : (
            t('notApplicable')
          ),
      },
      {
        id: 'amount',
        header: t('Columns.amount'),
        align: 'left',
        visible: activeTab === 'payment',
        template: ({ data }) => `$${formattedCurrency(String(data.Amount))}`,
      },
      {
        id: 'view',
        header: '',
        align: 'right',
        template: ({ data }) => (
          <Box gap="sm" jc="fe">
            {data.Status === 'pending' && (
              <Button
                id={`copy-url-${data.TransactionID}`}
                size="small"
                onClick={() => {
                  void navigator.clipboard.writeText(data.Link);
                }}
              >
                {t('copyUrl')}
              </Button>
            )}
          </Box>
        ),
      },
    ],
    rows: filteredList.map((item): { id: string; data: vp.ELinxHistoryItem } => ({
      id: String(item.ELinxRequestID || ''),
      data: item,
    })),
  };

  const exportColumns = tableProps.columns
    .filter((col) => col.id !== 'view')
    .map((col) => {
      if (col.id === 'requestStatus') {
        const { field, ...rest } = col;
        return {
          ...rest,
          template: ({ data }: { data: vp.ELinxHistoryItem }) => (
            <Box flx="row" ai="center">
              <Badge label={capitalizeFirstLetter(data.Status)} color={GetColorStatus(data.Status)} />
            </Box>
          ),
        };
      }
      return col;
    });

  const tableExportProps: vp.TableProps<vp.ELinxHistoryItem> = {
    ...tableProps,
    id: 'export-elinx-history-table',
    columns: exportColumns,
    rows: Object.values(paylinkHistoryExport.ELinxRequests || {}).map(
      (item): { id: string; data: vp.ELinxHistoryItem } => ({
        id: String(item.ELinxRequestID || ''),
        data: item,
      }),
    ),
  };

  const EmptyOrNoResults = () => {
    const isEmpty = initialLoad && tableProps.rows?.length === 0;
    const isNoSearch = !initialLoad;

    const message = isNoSearch ? t('noSearchMessage') : t('noRecordMessage');

    return isEmpty || isNoSearch ? (
      <Box flx="col" className={styles.elinxTableContainer} width="100%" gap="common">
        <Box flx="col" jc="center" gap="sm">
          {isNoSearch && (
            <Box ai="center" jc="center" height="auto">
              <Icon icon="search2" size="lg" />
            </Box>
          )}
          <Box ai="center" jc="center" pt="md" height="auto">
            {message}
          </Box>
        </Box>
      </Box>
    ) : null;
  };

  return (
    <>
      <Box flx="row" jc="center">
        <Box flx="col" gapSize="36px" width="100%">
          <Box jc="sb" ai="center" width="100%">
            <Title title={t('title')} />
            <ElinxHistoryExport onExport={handleExport} />
          </Box>
          <CardTabs.Root value={activeTab} onValueChange={handleTabChange}>
            <CardTabs.List aria-label="Elinx History">
              <CardTabs.Trigger value="account">{t('Tabs.account')}</CardTabs.Trigger>
              <CardTabs.Trigger value="payment">{t('Tabs.payment')}</CardTabs.Trigger>
            </CardTabs.List>
            {['account', 'payment'].map((tab) => (
              <CardTabs.Content key={tab} value={tab}>
                <Box flx="col" gap="sm">
                  <TableFiltersContainer filtersApplied={filtersApplied}>
                    <TableFiltersContainer.Trigger />
                    <TableFiltersContainer.Content>
                      <ElinxHistoryForm
                        requestStatus={requestStatus}
                        timeZone={timeZone}
                        searchTerm={searchTerm}
                        hasInvalidEndDate={hasInvalidEndDate}
                        shouldShowEndDateError={shouldShowEndDateError}
                        setShouldShowEndDateError={setShouldShowEndDateError}
                        initialTimeRange={[
                          parseISO(startDate || timeDifference).getTime() as vp.Timestamp,
                          parseISO(endDate || currentDateTime).getTime() as vp.Timestamp,
                        ]}
                        search={(searchParams) => {
                          const offset = setPageIndex(0);
                          updateQueryParams({
                            requestStatus: searchParams.requestStatus || '',
                            searchTerm: searchParams.searchTerm || '',
                            startDate: format(new Date(searchParams.timeRange[0]), 'yyyy-MM-dd'),
                            endDate: format(new Date(searchParams.timeRange[1]), 'yyyy-MM-dd'),
                            timeZone: searchParams.timeZone || '',
                          });
                          search(offset, searchParams);
                        }}
                      />
                    </TableFiltersContainer.Content>
                  </TableFiltersContainer>
                </Box>
                <div style={{ display: 'none' }}>
                  <Table {...tableExportProps} />
                </div>
                <Box>
                  <Table {...tableProps} />
                </Box>
                <EmptyOrNoResults />
                <Box width="100%" jc="center" mt="lg" mb="sm">
                  {pageCount > 1 && (
                    <Pagination
                      pageCount={pageCount}
                      forcePageNumber={pageIndex}
                      handlePageClick={(e) => {
                        const offset = setPageIndex(e.selected);
                        const searchParams = {
                          requestStatus,
                          searchTerm,
                          startDate: startDate || timeDifference,
                          endDate: endDate || currentDateTime,
                          timeZone: timeZone,
                        };
                        search(offset, searchParams);
                      }}
                    />
                  )}
                </Box>
              </CardTabs.Content>
            ))}
          </CardTabs.Root>
        </Box>
      </Box>
    </>
  );
};

export default ElinxHistory;
